import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { UrlParams } from 'common/types';
import { buildUrl } from 'common/util/location';
import Link from 'components/common/Link/Link';

import * as K from 'common/constants';
import { DashboardParam, GraphConfigType } from 'components/dashboards/types';
import { NavPlotToSearch } from 'components/dashboards/DashboardPage/modals/PlotZoom';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';

import { plotsStylesState } from 'styles/stylesProvider';
import './NoResults.scss';

import { getPlotSettings, substituteParameters } from '../dashboardData';

export interface NoResultsType {
  config: GraphConfigType;
  params: Array<DashboardParam>;
  message?: string;
}

/**
 * This component displays "No results" (or supplied text) as a clickable link to the graph or PQ page.
 *
 * @prop config            The config stanza for just this graph
 * @prop params            The current list of params and their values
 * @prop isDarkMode        true if we're using darkk mode
 * @prop startTime         The starting time
 * @prop endTime           The ending time
 */
const NoResults = ({
  config,
  params,
  message = 'No results',
}: NoResultsType): JSX.Element => {

  const [navToSearch, setNavToSearch] = useState(false);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const plotsStyles = useRecoilValue(plotsStylesState);

  // Let user click through to the PQ page to see what's up
  if (config.query) {
    const exploreHref = buildUrl(K.ROUTE.POWER_QUERY, { [K.URL_PARAM.FILTER]: substituteParameters(params, config.query), [K.URL_PARAM.START_TIME]: startTime, [K.URL_PARAM.END_TIME]: endTime } as UrlParams);
    return (
      <div className="NoResults">
        <Link className="NoResults__Link" href={exploreHref}>{message}</Link>
      </div>
    );
  }

  // Otherwise, we go to the graph page
  const plots = config.plots ? config.plots.map((plotConfig, plotIndex) => {
    const settings = getPlotSettings(config, plotConfig, params);
    return { ...settings, color: plotsStyles[plotIndex] };
  }) : undefined;
  const navToResults = !config.query ? <NavPlotToSearch graphConfig={config} plots={plots} doNavigate={navToSearch} onClose={(): void => setNavToSearch(false)} /> : undefined;
  return (
    <div className="NoResults">
      <a className="NoResults__Link" href="#" onClick={(): void => setNavToSearch(true)}>{message}</a>
      {navToResults}
    </div>
  );
};

export default NoResults;
